import React, {useEffect, useRef, useState} from 'react';
import {Card, Form, Button, Alert, Spinner, FormCheck, Dropdown, ProgressBar} from 'react-bootstrap';
import { apiService } from '../services/apiService';
import {useEnvironment} from "../contexts/EnvironmentContext.jsx";
import _ from "lodash";
import FileUpload from "@/components/FileUpload.jsx";
import FileUploadModal from "@/components/FileUploadModal.jsx";
import { RotateCcw, Mic, Plus, Image } from 'lucide-react';

const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
};

const AskQuestion = ({ user }) => {
    const {
        environment,        // 'development' or 'production'
        isDevelopment,      // boolean flag
        isProduction,       // boolean flag
        apiEndpoints,       // object with API endpoints
        changeEnvironment   // function to change environment
    } = useEnvironment();

    const [question, setQuestion] = useState('');
    const [conversationId, setConversationId] = useState('');
    const [isRestarting, setIsRestarting] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showRemember, setShowRemember] = useState(false);
    const [memoryInput, setMemoryInput] = useState('');

    const [selectedGroupId, setSelectedGroupId] = useState(!_.isEmpty(user.groupData[0]) ? user.groupData[0].group_id : -1);
    const [selectedMemoryType, setSelectedMemoryType] = useState(!_.isEmpty(user.groupData[0]) ? user.groupData[0].group_name : '');
    const [groupName, setGroupName] = useState(!_.isEmpty(user.groupData[0]) ? user.groupData[0].group_name : '');
    const [response, setResponse] = useState([]);
    const [error, setError] = useState('');
    const [isCheckedNewConvo, setIsCheckedNewConvo] = useState(false);

    const [conversation, setConversation] = useState([]);
    const [dailyOverview, setDailyOverview] = useState('');
    const [isLoadingOverview, setIsLoadingOverview] = useState(false);
    const hasLoadedOverview = useRef(false);

    useEffect(() => {
        if (hasLoadedOverview.current) return;
        hasLoadedOverview.current = true;
        fetchDailyOverview();
    }, []);

    const fetchDailyOverview = async () => {
        setIsLoadingOverview(true);
        try {
            const sgroup = _.find(user.groupData, {group_id: selectedGroupId});
            const gmember = _.find(sgroup?.group_members, {uid: user.id});
            const sessionId = Date.now();
            const groupId = selectedMemoryType === 'Private' ? '' : selectedGroupId;
            const roleId = gmember?.role_id || '';

            const buildForm = (text, sid) => {
                const f = new FormData();
                f.append('sessionId', sid);
                f.append('uid', user.id);
                f.append('text', text);
                f.append('groupId', groupId);
                f.append('group_role_id', roleId);
                f.append('fileCount', '0');
                return f;
            };

            const todayStr = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
            const result1 = await apiService.askQuestion(apiEndpoints.ask, buildForm(`what do i have scheduled today ${todayStr} and what suggestions do you have for my day`, sessionId));
            const output1 = _.find(_.get(result1, 'aiResponse', []), item => item.output)?.output || '';

            // const result2 = await apiService.askQuestion(apiEndpoints.ask, buildForm('what suggestions do you have for my day', sessionId));
            // const output2 = _.find(_.get(result2, 'aiResponse', []), item => item.output)?.output || '';

            setDailyOverview( output1 );
        } catch (err) {
            console.error('Error fetching daily overview:', err);
        } finally {
            setIsLoadingOverview(false);
        }
    };

    /** * **************************************
     * File upload stuff
     */
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadError, setUploadError] = useState('');
    const [uploadSuccess, setUploadSuccess] = useState('');
    const [textUploadSuccess, setTextUploadSuccess] = useState('');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [selectedMainFiles, setSelectedMainFiles] = useState([]); // New state for main form files
    const fileInputRef = useRef(null);
    const [isUploading, setIsUploading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [uploadResponse, setUploadResponse] = useState([]);

    const handleFileChange = (event) => {
        const files = Array.from(event.target.files);
        setSelectedFiles(files);
        setUploadProgress(0);
        setUploadError('');
        setUploadSuccess('');
    };

    const handleFileUploadResponse = (uploadResponse) => {
        setUploadResponse(uploadResponse);
        setShowModal(true);
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            // Add new files to existing list instead of replacing
            setSelectedMainFiles(prevFiles => {
                const updatedFiles = [...prevFiles, ...files];
                console.log('Added files:', files.map(f => ({ name: f.name, type: f.type, size: f.size })));
                console.log('Total files now:', updatedFiles.length);
                return updatedFiles;
            });
        }

        // Clear the input so the same file can be selected again if needed
        if (e.target) {
            e.target.value = '';
        }
    };

    const handleFileIconClick = () => {
        fileInputRef.current?.click();
    };

    const removeSelectedFiles = () => {
        setSelectedMainFiles([]);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const removeSelectedFile = (indexToRemove) => {
        const updatedFiles = selectedMainFiles.filter((_, index) => index !== indexToRemove);
        setSelectedMainFiles(updatedFiles);

        // If no files left, clear the input
        if (updatedFiles.length === 0 && fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    /** * ************************************** */

    const handleModalClose = () => {
        setUploadProgress(0);
        setUploadError('');
        setUploadSuccess('');
        setSelectedFiles([]);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        setShowModal(false);
    };

    const handleUploadText = async (event) => {
        event.preventDefault();
        const uploadText = question;
        console.log('[handleUploadText]-uploadText---->',uploadText);
        console.log('[handleUploadText]-selectedMainFiles---->',selectedMainFiles);

        if (_.isEmpty(uploadText) && selectedMainFiles.length == 0 ) {
            setUploadError('Please enter text to remember or an image');
            setIsUploading(false);
            return;
        }

        try {
            setIsUploading(true);
            setUploadError('');

            const metadata = {
                description: uploadText,
                uid: user.uid,
                approvestep: true,
                group_id: selectedGroupId
            }

            const uploadResponse = await apiService.uploadFile(
                apiEndpoints.upload,
                selectedMainFiles,
                metadata,
                (progress) => setUploadProgress(progress)
            );
            console.log('[handleUploadText]----uploadResponse->',uploadResponse);
            setUploadResponse(uploadResponse);
            handleFileUploadResponse(uploadResponse);

            // if (typeof uploadResponse === 'object' && uploadResponse !== null) {
            //     setUploadResponse(uploadResponse);
            //     handleFileUploadResponse(uploadResponse);
            // } else if (typeof uploadResponse === 'string') {
            //     setUploadError(uploadResponse);
            // }

            setSelectedFiles([]);
            setQuestion('');

        } catch (error) {
            console.error('Upload error:', error);
            setUploadError(
                error.response?.data?.message ||
                error.message ||
                'Failed to upload file. Please try again.'
            );
        } finally {
            setIsUploading(false);
        }
    };

    const handleClear = async (event) => {
        event.preventDefault();
        setIsRestarting(true);
        setConversationId('');
        setQuestion('');
        setResponse([]);
        setIsRestarting(false);
        setConversation([]);
        setError('');
    }

    const handleRemember = async (event) => {
        event.preventDefault();
        console.log('[handleRemember]----->',);
        console.log('[handleRemember]--selectedFiles--->',selectedFiles.length);
        if (selectedFiles.length === 0) {
            await handleUploadText(event);
            return;
        }
        if (selectedFiles.length > 2) {
            setUploadError('Please only upload a maximum of 2 files');
            return;
        }
        try {
            setIsUploading(true);
            setUploadError('');

            const responses = [];

            const metadata = {
                description: question, // Changed from xtraInfo to question
                userid: user.id,
                approvestep: true,
                group_id: selectedGroupId
            };
            const fileResponse = await apiService.uploadFile(
                apiEndpoints.upload,
                selectedFiles,
                metadata,
                (progress) => setUploadProgress(Math.floor((progress)))
            );

            console.log('[handleRemember---fileResponse-->',fileResponse);

            if (typeof fileResponse === 'object' && fileResponse !== null) {
                setResponse(fileResponse);
                handleFileUploadResponse(fileResponse);
            } else if (typeof fileResponse === 'string') {
                setUploadError(fileResponse);
            }

            setSelectedFiles([]);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }


        } catch (error) {
            console.error('Upload error:', error);
            setUploadError(
                error.response?.data?.message ||
                error.message ||
                'Failed to upload files. Please try again.'
            );
        } finally {
            setIsUploading(false);
        }
    };

    const questionInputRef = useRef(null);

    const focusTextarea = () => {
        if (questionInputRef.current) {
            questionInputRef.current.focus();
        }
    };

    const handleRestart = async (e) => {
        setIsRestarting(true);
        setConversationId('');
        setQuestion('');
        setResponse([]);
        setIsRestarting(false);
        setConversation([]);
        setError('');
    }

    const handleCancel = async (e) => {
        setShowRemember(false);
    }

    const handleRememberOLD = async (e) => {
        setShowRemember(!showRemember);
    }

    const handleMemorySubmit = async (e) => {
        e.preventDefault();

        if (!memoryInput.trim()) {
            setError('Please enter something to remember');
            return;
        }

        try {
            setIsSubmitting(true);
            setError('');

            setResponse([...response, {
                output: `Memory saved: ${memoryInput}`
            }]);

            setMemoryInput('');
            setShowRemember(false);
        } catch (err) {
            console.error('Error saving memory:', err);
            setError('Failed to save memory. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        let convo = conversation;

        if (!question.trim() && selectedMainFiles.length === 0) {
            setError('Please enter a question or select files');
            return;
        }

        try {
            setIsSubmitting(true);
            setError('');

            let conversationIdToUse = conversationId;
            if (conversationId == '' || isCheckedNewConvo) {
                conversationIdToUse = Date.now();
                setConversationId(conversationIdToUse);
                convo = [{
                    input: question,
                    files: selectedMainFiles.map(file => file.name) // Changed to files array
                }];
            } else {
                convo = _.union(response, [{
                    input: question,
                    files: selectedMainFiles.map(file => file.name) // Changed to files array
                }]);
            }

            // Create FormData to include both text and multiple files
            const formdata = new FormData();
            formdata.append('sessionId', conversationIdToUse);
            formdata.append('uid', user.id);
            formdata.append('text', question);
            formdata.append('groupId', selectedMemoryType === 'Private' ? '' : selectedGroupId);

            const sgroup = _.find(user.groupData,{group_id: selectedGroupId});
            console.log('--groupData--->', sgroup);
            const gmember = _.find(sgroup.group_members,{uid: user.id});
            console.log('--gmember--->', gmember);
            formdata.append('group_role_id', gmember.role_id);

            // Append multiple files
            selectedMainFiles.forEach((file, index) => {
                formdata.append(`file${index}`, file);
            });

            // Also send the total count of files
            formdata.append('fileCount', selectedMainFiles.length.toString());

            console.log('[handleSubmit]--formdata--->',formdata);
            const result = await apiService.askQuestion(apiEndpoints.ask, formdata);

            convo = _.union(convo, result.aiResponse);
            setQuestion('');
            setSelectedMainFiles([]); // Clear the selected files
            if (fileInputRef.current) {
                fileInputRef.current.value = ''; // Reset file input
            }
            setResponse(convo);
        } catch (err) {
            console.error('Error submitting question:', err);
            setError('Failed to get a response. Please try again.');
        } finally {
            setIsSubmitting(false);
            setTimeout(focusTextarea, 0);
        }
    };

    const handleKeyDown = (e) => {
        // Check if Enter was pressed without Shift (Shift+Enter allows for new lines)
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault(); // Prevent the default action (new line)

            // Submit the form if question is not empty or files are selected and not already submitting
            if ((question.trim() || selectedMainFiles.length > 0) && !isSubmitting) {
                handleSubmit(e);
            }
        }
    };

    const renderQuestionCard = () => (
        <div className="ask-question-panel">
            {/* Greeting + Avatar section */}
            <div className="ask-panel-top text-center">
                {/* Avatar: globe/orb image - user will provide */}
                <div className="ask-avatar-wrap mb-3">
                    <div className="ask-avatar-placeholder">
                        {/* Globe avatar image goes here - replace src when user provides */}
                        <img
                            src="/globe-avatar.png"
                            alt="Knoko"
                            className="ask-avatar-img"
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                    </div>
                </div>

                {/* Greeting */}
                <h2 className="ask-greeting">
                    {getGreeting()}, {user.firstName}!
                </h2>
                <p className="ask-subtitle">Here is your overview for the day</p>
            </div>

            {/* Chat Messages / Daily Overview */}
            <div className="ask-panel-messages">
                {/* Show daily overview when no conversation yet */}
                {_.isEmpty(response) && (
                    <div className="daily-overview">
                        {isLoadingOverview ? (
                            <div className="d-flex align-items-center gap-2" style={{ color: '#aaa', fontSize: '13px' }}>
                                <Spinner animation="border" size="sm" />
                                <span>Loading your daily overview...</span>
                            </div>
                        ) : dailyOverview ? (
                            <p
                                style={{ fontSize: '13px', color: '#444', lineHeight: '1.8', margin: 0, whiteSpace: 'pre-wrap' }}
                                dangerouslySetInnerHTML={{ __html: _.replace(dailyOverview, /\n/g, '<br>') }}
                            />
                        ) : null}
                    </div>
                )}

                {/* Regular chat conversation */}
                {!_.isEmpty(response) && (
                    <div className="chat-messages mb-3">
                        {response.map((item, index) => {
                            const isOutput = _.get(item, 'output');
                            const html = isOutput ? item.output : item.input;
                            return (
                                <div className={`d-flex w-100 ${isOutput ? '' : 'justify-content-end'}`} key={index}>
                                    <div
                                        className={`chat-bubble mb-2 ${isOutput ? 'chat-bubble-output' : 'chat-bubble-input'}`}
                                        style={{ maxWidth: '85%' }}
                                    >
                                        <p
                                            style={{ margin: 0, fontSize: '13px', lineHeight: '1.5' }}
                                            dangerouslySetInnerHTML={{__html: _.replace(html, /\n/g, '<br>')}}
                                        />
                                        {item.files && item.files.length > 0 && (
                                            <div className="mt-1">
                                                <small style={{color: '#666'}}>📎 {item.files.join(', ')}</small>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Bottom Input Bar */}
            <div className="ask-panel-input">
                {/* Selected files preview */}
                {selectedMainFiles.length > 0 && (
                    <div className="mb-2">
                        {selectedMainFiles.map((file, index) => (
                            <div key={index} className="d-flex align-items-center gap-2 mb-1"
                                style={{ padding: '6px 10px', backgroundColor: '#f0f8ff', borderRadius: '8px', fontSize: '12px' }}>
                                <span>📎</span>
                                <span>{file.name}</span>
                                <button type="button" onClick={() => removeSelectedFile(index)}
                                    style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', marginLeft: 'auto', padding: '0' }}>
                                    ✕
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {error && <Alert variant="danger" className="mb-2 py-2" style={{ fontSize: '12px' }}>{error}</Alert>}
                {uploadError && <Alert variant="danger" className="mb-2 py-2" style={{ fontSize: '12px' }}>{uploadError}</Alert>}
                {uploadProgress > 0 && <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} animated={isUploading} className="mb-2" />}

                <Form onSubmit={handleSubmit}>
                    <div className="ask-input-bar">
                        {/* Left icons */}
                        <div className="ask-input-left">
                            <button type="button" className="ask-icon-btn" onClick={handleFileIconClick} disabled={isSubmitting}>
                                <Plus size={16} color="#07BCD9" />
                            </button>
                            <button type="button" className="ask-icon-btn" onClick={handleFileIconClick} disabled={isSubmitting}>
                                <Image size={16} color="#07BCD9" />
                            </button>
                            <input type="file" ref={fileInputRef} onChange={handleFileSelect}
                                style={{ display: 'none' }} accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.txt,.csv,.xlsx" multiple />

                            {/* Group dropdown */}
                            <Dropdown>
                                <Dropdown.Toggle className="ask-group-toggle" id="group-dropdown">
                                    {groupName || selectedMemoryType}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    {user.groupData && user.groupData.map((group, index) => (
                                        <Dropdown.Item key={group.group_id || index}
                                            onClick={() => { setSelectedGroupId(group.group_id); setSelectedMemoryType(group.group_name); setGroupName(group.group_name); }}
                                            active={selectedGroupId === group.group_id}>
                                            {group.group_name}
                                        </Dropdown.Item>
                                    ))}
                                    <Dropdown.Item onClick={() => { setSelectedMemoryType('Private'); setGroupName('Private'); }} active={selectedMemoryType === 'Private'}>
                                        Private
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>

                        {/* Text input */}
                        <textarea
                            className="ask-textarea"
                            rows={1}
                            placeholder="Ask about your memories or anything else..."
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={isSubmitting}
                            ref={questionInputRef}
                        />

                        {/* Right: mic + action buttons */}
                        <div className="ask-input-right">
                            <button type="button" className="ask-icon-btn" disabled={isSubmitting}>
                                <Mic size={16} color="#07BCD9" />
                            </button>

                            <button
                                type="button"
                                className="ask-action-btn ask-remember-btn"
                                onClick={handleRemember}
                                disabled={isSubmitting || isUploading}
                            >
                                {isUploading ? <Spinner as="span" animation="border" size="sm" /> : 'REMEMBER'}
                            </button>

                            <button
                                type="submit"
                                className="ask-action-btn ask-ask-btn"
                                disabled={isSubmitting || (!question.trim() && selectedMainFiles.length === 0)}
                            >
                                {isSubmitting ? <Spinner as="span" animation="border" size="sm" /> : 'ASK'}
                            </button>
                        </div>
                    </div>
                </Form>
            </div>

            <FileUploadModal
                responseArr={uploadResponse}
                show={showModal}
                onHide={handleModalClose}
                user={user}
                onApprovalComplete={(approvalResponse) => {
                    setTimeout(() => {
                        removeSelectedFiles();
                        setTextUploadSuccess(_.get(approvalResponse,'responseMessage', ''))
                    }, 500);
                }}
            />
        </div>
    );

    const renderRememberCard = () => (
        <FileUpload user={user} handleCancel={handleCancel}/>
    );

    return showRemember ? renderRememberCard() : renderQuestionCard();
};

export default AskQuestion;
